import React from "react";
import { motion } from "framer-motion";
import { ProviderIcon } from "../shared/ProviderIcon";

const PROVIDER_CONFIG = {
	openai: {
		name: "ChatGPT",
		cardClass: "provider-card--openai",
	},
	gemini: {
		name: "Gemini",
		cardClass: "provider-card--gemini",
	},
	claude: {
		name: "Claude",
		cardClass: "provider-card--claude",
	},
};

function toPercent(rate) {
	if (rate == null) return 0;
	return rate <= 1 ? rate * 100 : rate;
}

function getCitationRateClass(rate) {
	if (rate >= 30) return "citation-rate--high";
	if (rate >= 10) return "citation-rate--medium";
	return "citation-rate--low";
}

function getCitationFillClass(rate) {
	if (rate >= 30) return "provider-citation-card__fill--high";
	if (rate >= 10) return "provider-citation-card__fill--medium";
	return "provider-citation-card__fill--low";
}

export function ProviderCitationCards({ perProvider = {} }) {
	const providers = ["openai", "gemini", "claude"].filter((p) => perProvider[p]);

	if (!providers.length) return null;

	return (
		<div className="provider-citation-cards">
			{providers.map((provider, index) => {
				const data = perProvider[provider];
				const config = PROVIDER_CONFIG[provider];
				const citationRate = toPercent(data.citation_rate);
				const mentionRate = toPercent(data.mention_rate);
				const citedCount = data.cited_count || 0;
				const mentionedCount = data.mentioned_count || 0;
				const totalQueries = data.queries_tested || 0;

				return (
					<motion.div
						key={provider}
						className={`provider-citation-card ${config.cardClass}`}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: index * 0.1 }}
					>
						<div className="provider-citation-card__header">
							<div className="provider-citation-card__icon-wrap">
								<ProviderIcon provider={provider} size={22} />
							</div>
							<h3 className="provider-citation-card__name">{config.name}</h3>
						</div>

						<div className="provider-citation-card__rate-row">
							<span className="provider-citation-card__rate-label">Cited</span>
							<span
								className={`provider-citation-card__rate-value ${getCitationRateClass(citationRate)}`}
							>
								{citationRate.toFixed(0)}%
							</span>
						</div>
						<div className="provider-citation-card__track">
							<motion.div
								className={`provider-citation-card__fill ${getCitationFillClass(citationRate)}`}
								initial={{ width: 0 }}
								animate={{ width: `${Math.min(citationRate, 100)}%` }}
								transition={{ duration: 0.8, delay: index * 0.1 + 0.3 }}
							/>
						</div>

						<div className="provider-citation-card__rate-row">
							<span className="provider-citation-card__rate-label">Mentioned</span>
							<span className="provider-citation-card__rate-value provider-citation-card__rate-value--mention">
								{mentionRate.toFixed(0)}%
							</span>
						</div>
						<div className="provider-citation-card__track provider-citation-card__track--thin">
							<motion.div
								className="provider-citation-card__fill provider-citation-card__fill--mention"
								initial={{ width: 0 }}
								animate={{ width: `${Math.min(mentionRate, 100)}%` }}
								transition={{ duration: 0.8, delay: index * 0.1 + 0.4 }}
							/>
						</div>

						<div className="provider-citation-card__counts">
							<p>
								<strong>{citedCount}</strong> of {totalQueries} cited
							</p>
							<p>
								<strong>{mentionedCount}</strong> of {totalQueries} mentioned
							</p>
						</div>

						{data.avg_position != null && (
							<div className="provider-citation-card__position">
								Avg source position:{" "}
								<strong>#{Number(data.avg_position).toFixed(1)}</strong>
							</div>
						)}
					</motion.div>
				);
			})}
		</div>
	);
}

export default ProviderCitationCards;
